import { useTrackUiEventFromClickEvent } from '@upheal/shared/utils/analytics/ui-tracking';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import React, { useCallback, useState } from 'react';
import {
    PORTAL_ABOVE_MOBILE_MENU_ID,
    PORTAL_ABOVE_MODAL_ID,
} from '@upheal/utils/App.constants';
import SuiBox from '@upheal/design-system/components/legacy/Sui/SuiBox';
import { LinearProgress } from '@mui/material';
import { useAppState } from '@upheal/shared/state';
import { trackPageView } from '@snowplow/browser-tracker';
import {
    ERROR_NOT_FOUND_ROUTE,
    FORGOT_PASSWORD_ROUTE,
    LOGIN_ROUTE,
    LOGIN_ROUTE_AUTH_EXPIRED,
    SIGNUP_ROUTE,
    UNUPLOADED_SESSIONS_ROUTE,
    ZOOM_APP_HOME_ROUTE,
} from '@upheal/shared/routes';
import {
    CARD_ADDED_SUCCESS_ROUTE,
    CHECKOUT_CANCEL_ROUTE,
    CHECKOUT_PAY_ROUTE,
    CHECKOUT_SUCCESS_ROUTE,
} from '@upheal/client-billing/routes';
import Error404 from './components/Errors/404';
import ErrorGeneric from './components/Errors/ErrorGeneric';
import RequireAuth from '@upheal/auth/RequireAuth';
import { browserStorage } from '@upheal/shared/utils/cache/browserStorage';
import { useEffectOnce, useMount } from 'react-use';
import GenericErrorBoundary from '@upheal/shared/components/shared/ErrorBoundary/GenericErrorBoundary';
import { SWAL_UPHEAL_TARGET_ELEMENT_ID } from '@upheal/design-system/vendor/swal-upheal';
import DebugModeFloatingButton from '@upheal/utils/debug-mode/DebugModeFloatingButton';
import { useLocalStorage } from '@upheal/utils/hooks/useLocalStorage';
import { useTerminateInactiveUserSession } from '@upheal/shared/utils/useTerminateInactiveUserSession';
import { UserRole } from '@upheal/shared/types';
import UnsupportedBrowserDetection from './components/misc/UnsupportedBrowserDetection';
import { apiServerController } from '@upheal/shared/utils/api';
import { validateSessionOnInit } from '@upheal/shared/utils/auth/validateSessionOnInit';
import { isTherapySessionInProgress } from '@upheal/shared/utils/syncBrowserTabs';
import {
    hasAudioRecorder,
    hasOnlineCall,
    isDefaultApp,
    isWhiteLabelApp,
} from '@upheal/design-system/theme/legacy/is-whitelabel';
import WhiteLabelFallbackPage from '@upheal/telehealth/WhiteLabel/WhiteLabelFallbackPage';
import { Z_INDEX_MOBILE_MENU } from '@upheal/design-system/components/Sidebar/constants';
import { waitForPostHogFlags } from '@upheal/shared/hooks/useWaitForPostHogFlags';
import { useIsNewVideoCallingEnabled } from '@upheal/telehealth/hooks/useIsNewVideoCallingEnabled';
import { VideoSessionDependencyInjectionProvider } from '@upheal/telehealth/VideoSessionDependencyInjectionContext';
import { useWhiteLabelOtpAuth } from '@upheal/telehealth/WhiteLabel/useWhiteLabelOtpAuth';
import { PROGRESS_INSIGHTS_DEMO_ROUTE } from './demo/progressInsights/constants';

const App = React.lazy(() => import('./App' /* webpackChunkName: "App" */));
const ProgressInsightsDemoApp = React.lazy(() =>
    import(
        './demo/progressInsights/ProgressInsightsDemoApp' /* webpackChunkName: "ProgressInsightsDemoApp" */
    ).then((module) => ({ default: module.ProgressInsightsDemoApp })),
);
const ClientSignIn = React.lazy(
    () =>
        import(
            './components/SignIn/ClientSignIn' /* webpackChunkName: "ClientSignIn" */
        ),
);
const ClientOtpSignIn = React.lazy(() =>
    import(
        './components/SignIn/ClientOtpSignIn' /* webpackChunkName: "ClientOtpSignIn" */
    ).then((module) => ({
        default: module.ClientOtpSignIn,
    })),
);

const TherapistSessionVideo = React.lazy(
    () =>
        import(
            '@upheal/telehealth/legacy/TherapistSessionVideo'
            /* webpackChunkName: "TherapistSessionVideo" */
        ),
);
const SelfScheduling = React.lazy(
    () =>
        import(
            './components/SelfScheduling/SelfScheduling'
            /* webpackChunkName: "SelfScheduling" */
        ),
);
const SelfSchedulingPreview = React.lazy(
    () =>
        import(
            './components/SelfScheduling/SelfSchedulingPreview'
            /* webpackChunkName: "SelfSchedulingPreview" */
        ),
);
const AudioRecorder = React.lazy(
    () =>
        import(
            './components/AudioRecorder/AudioRecorder'
            /* webpackChunkName: "AudioRecorder" */
        ),
);
const UnuploadedSessions = React.lazy(
    () =>
        import(
            './components/UnuploadedSessions/UnuploadedSessions'
            /* webpackChunkName: "UnuploadedSessions" */
        ),
);

const BookSession = React.lazy(() =>
    import(
        'components/BookSession/BookSession'
        /* webpackChunkName: "BookSession" */
    ).then((module) => ({ default: module.BookSession })),
);

const UphealerLinkApp = React.lazy(
    () =>
        import(
            '@upheal/telehealth/VideoSessionEntry'
            /* webpackChunkName: "UphealerLinkApp" */
        ),
);

const UphealerLinkAppLegacy = React.lazy(
    () =>
        import(
            'components/SignIn/SessionEntry'
            /* webpackChunkName: "UphealerLinkAppLegacy" */
        ),
);

const SignIn = React.lazy(
    () => import('./components/SignIn/SignIn' /* webpackChunkName: "SignIn" */),
);
const SamlCallback = React.lazy(() =>
    import(
        './components/SignIn/SamlCallback' /* webpackChunkName: "SamlCallback" */
    ).then((module) => ({ default: module.SamlCallback })),
);
const FeaturebaseSsoCallback = React.lazy(() =>
    import(
        './components/SignIn/FeaturebaseSsoCallback' /* webpackChunkName: "FeaturebaseSsoCallback" */
    ).then((module) => ({ default: module.FeaturebaseSsoCallback })),
);
const SamlTestCallback = React.lazy(() =>
    import(
        './components/Administration/OrganizationSettings/SingleSignOnSettings/SamlSSO/SamlTestCallback' /* webpackChunkName: "SamlTestCallback" */
    ).then((module) => ({ default: module.SamlTestCallback })),
);
const GoogleSignUp = React.lazy(() =>
    import(
        './components/SignUp/GoogleSignUp' /* webpackChunkName: "GoogleSignUp" */
    ).then((module) => ({ default: module.GoogleSignUp })),
);

const TherapistSignUp = React.lazy(
    () =>
        import(
            './components/SignUp/TherapistSignUp' /* webpackChunkName: "TherapistSignUp" */
        ),
);
const Registration = React.lazy(
    () =>
        import(
            './components/SignUp/Registration' /* webpackChunkName: "Registration" */
        ),
);
const ForgotPassword = React.lazy(
    () =>
        import(
            './components/RecoverPassword/ForgotPassword' /* webpackChunkName: "ForgotPassword" */
        ),
);
const NewPassword = React.lazy(
    () =>
        import(
            './components/RecoverPassword/NewPassword' /* webpackChunkName: "NewPassword" */
        ),
);

const ZoomApp = React.lazy(
    () =>
        import(
            './components/ZoomApp/ZoomApp' /* webpackChunkName: "ZoomApp" */
        ),
);

const ZoomAppInstall = React.lazy(
    () =>
        import(
            './components/ZoomApp/ZoomAppInstall' /* webpackChunkName: "ZoomAppInstall" */
        ),
);

const ZoomAppAuth = React.lazy(
    () =>
        import(
            './components/ZoomApp/ZoomAppAuth' /* webpackChunkName: "ZoomAppAuth" */
        ),
);
const ClientPortal = React.lazy(
    () => import('./components/ClientPortal/ClientPortal'),
);

const CheckoutPay = React.lazy(() =>
    import(
        './components/Checkout/CheckoutPay' /* webpackChunkName: "CheckoutPay" */
    ).then((module) => ({ default: module.CheckoutPay })),
);

const CheckoutSuccess = React.lazy(() =>
    import(
        '@upheal/client-billing/components/Checkout/CheckoutSuccess' /* webpackChunkName: "CheckoutSuccess" */
    ).then((module) => ({ default: module.CheckoutSuccess })),
);

const CheckoutCancel = React.lazy(() =>
    import(
        '@upheal/client-billing/components/Checkout/CheckoutCancel' /* webpackChunkName: "CheckoutCancel" */
    ).then((module) => ({ default: module.CheckoutCancel })),
);

const CardAddedSuccess = React.lazy(() =>
    import(
        '@upheal/client-billing/components/Checkout/CardAddedSuccess' /* webpackChunkName: "CardAddedSuccess" */
    ).then((module) => ({ default: module.CardAddedSuccess })),
);

const CodeSplittingRouting = () => {
    const history = useHistory();
    const [isInitiated, setIsInitiated] = useState(false);
    const [isZoomApp, setIsZoomApp] = useLocalStorage('ZOOM/isZoomApp', false); // eventually we want to have separate build for zoom app
    const { user, getMe } = useAppState();

    const globalUiTracking = useTrackUiEventFromClickEvent();
    const authToken = browserStorage.loadAuthToken();
    useTerminateInactiveUserSession({
        condition: Boolean(
            user &&
                user.role !== UserRole.Client &&
                authToken &&
                !authToken.rememberMe,
        ),
    });
    const isNewVideoCallingEnabled = useIsNewVideoCallingEnabled();

    const handleZoomAppInit = useCallback(() => {
        setIsZoomApp(true);
    }, [setIsZoomApp]);

    useEffectOnce(() => {
        apiServerController.onRefreshTokenFailure = () => {
            if (!isTherapySessionInProgress()) {
                window.location.href = LOGIN_ROUTE_AUTH_EXPIRED;
            }
        };

        return () => {
            apiServerController.onRefreshTokenFailure = undefined;
        };
    });

    // White-label OTP auth ideally belongs in specific routes, but app initialization
    // (PostHog, user state, isInitiated gate) happens here - refactoring would be non-trivial.
    const otpAuthState = useWhiteLabelOtpAuth();
    useMount(async () => {
        // @ts-ignore
        window._routerHistory = history;

        const authToken = browserStorage.loadAuthToken();
        const shouldTryToAuth = Boolean(
            otpAuthState.status === 'not_applicable' &&
                authToken &&
                !authToken.isClient,
        );

        // Validate session for non-rememberMe users.
        // If invalid, onRefreshTokenFailure already triggered the redirect.
        if (shouldTryToAuth) {
            const { isValid } = await validateSessionOnInit();
            if (!isValid) {
                return;
            }
        }

        try {
            if (otpAuthState.status === 'authenticating') {
                await otpAuthState.promise;
            } else if (shouldTryToAuth) {
                await getMe();
            } else if (history.location.pathname === '/') {
                history.push(LOGIN_ROUTE);
            }
            // Hold on the first render until we have the flags loaded
            // This is to ensure experiments and flags are resolved (not undefined) from the start
            await waitForPostHogFlags();
        } catch {
            // swallow error
        } finally {
            setIsInitiated(true);

            trackPageView();
            history.listen(() => trackPageView());
        }
    });

    const pageLoader = (
        <SuiBox sx={{ width: '100%' }}>
            <LinearProgress />
        </SuiBox>
    );

    return (
        <div {...globalUiTracking}>
            <style>
                {`
                 body {
                    /* This is needed for PORTAL_ABOVE_MODAL to work correctly,
                       but to be honest I don't fully understand why 😅
                    */
                    height: 100vh;
                 }

                 /* Safari mobile viewport height fixes */
                 @supports (height: 100dvh) {
                    body {
                        height: 100dvh;
                    }
                 }

                 #${PORTAL_ABOVE_MODAL_ID} {
                    z-index: 99999;
                 }

                 #${PORTAL_ABOVE_MODAL_ID} > * {
                    z-index: 99999;
                 }

                 #${PORTAL_ABOVE_MOBILE_MENU_ID},
                 #${PORTAL_ABOVE_MOBILE_MENU_ID}  > * {
                    z-index: ${Z_INDEX_MOBILE_MENU};
                 }
                `}
            </style>
            <div style={{ zIndex: 99999 }} id={PORTAL_ABOVE_MODAL_ID} />
            <div
                style={{ zIndex: Z_INDEX_MOBILE_MENU }}
                id={PORTAL_ABOVE_MOBILE_MENU_ID}
            />
            <div style={{ zIndex: 99999 }} id={SWAL_UPHEAL_TARGET_ELEMENT_ID} />
            {!isInitiated && pageLoader}
            {isInitiated && (
                <GenericErrorBoundary>
                    <React.Suspense fallback={pageLoader}>
                        <Switch>
                            {/* AUTH ROUTES */}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path={LOGIN_ROUTE}
                                    component={SignIn}
                                    key="signIn"
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path="/auth/sso/saml/callback"
                                    component={SamlCallback}
                                    key="samlCallback"
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path="/auth/sso/saml/test-callback"
                                    component={SamlTestCallback}
                                    key="samlTestCallback"
                                />
                            )}
                            {isDefaultApp && (
                                <RequireAuth
                                    exact
                                    path="/sso/featurebase"
                                    component={FeaturebaseSsoCallback}
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path={'/signup/google'}
                                    component={GoogleSignUp}
                                    key="googleSignUp"
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path={SIGNUP_ROUTE}
                                    component={Registration}
                                />
                            )}
                            {/* END AUTH ROUTES */}

                            {/* ACCOUNT MANAGEMENT ROUTES */}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path="/invite"
                                    component={TherapistSignUp}
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path="/invite/:inviteId"
                                    component={TherapistSignUp}
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path={FORGOT_PASSWORD_ROUTE}
                                    component={ForgotPassword}
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path="/password-reset/:inviteId"
                                    component={NewPassword}
                                />
                            )}
                            {/* END ACCOUNT MANAGEMENT ROUTES */}

                            {/* SCHEDULING ROUTES */}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path="/self-scheduling/:therapistId/:appointmentTypeId"
                                    render={(routerProps) => (
                                        <SelfScheduling {...routerProps} />
                                    )}
                                />
                            )}
                            {isDefaultApp && (
                                <RequireAuth
                                    exact
                                    path="/self-scheduling-preview/:appointmentTypeId"
                                    render={(routerProps) => (
                                        <SelfSchedulingPreview
                                            {...routerProps}
                                        />
                                    )}
                                />
                            )}
                            {/* END SCHEDULING ROUTES */}

                            {/* CLIENT PORTAL ROUTES */}
                            {isDefaultApp && (
                                <Route path={'/client-portal'}>
                                    <ClientPortal />
                                </Route>
                            )}
                            {/* END CLIENT PORTAL ROUTES */}

                            {/* CONSENT ROUTES */}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path="/consent/:inviteId"
                                    render={(routerProps) => (
                                        <ClientSignIn
                                            consentsOnly={true}
                                            {...routerProps}
                                        />
                                    )}
                                />
                            )}
                            {/* END CONSENT ROUTES */}

                            {/* CHECKOUT ROUTES */}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path={CHECKOUT_PAY_ROUTE}
                                    component={CheckoutPay}
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path={CHECKOUT_SUCCESS_ROUTE}
                                    component={CheckoutSuccess}
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path={CHECKOUT_CANCEL_ROUTE}
                                    component={CheckoutCancel}
                                />
                            )}
                            {isDefaultApp && (
                                <Route
                                    exact
                                    path={CARD_ADDED_SUCCESS_ROUTE}
                                    component={CardAddedSuccess}
                                />
                            )}
                            {/* END CHECKOUT ROUTES */}

                            {/* AUDIO RECORDER ROUTES */}
                            {(isDefaultApp || hasAudioRecorder) && (
                                <Redirect
                                    exact
                                    from="/recorder/:contractIdOrUnassigned"
                                    to="/recorder?contractId=:contractIdOrUnassigned"
                                />
                            )}
                            {(isDefaultApp || hasAudioRecorder) && (
                                <RequireAuth
                                    exact
                                    path="/recorder"
                                    render={(routerProps) => (
                                        <>
                                            <UnsupportedBrowserDetection />
                                            <AudioRecorder {...routerProps} />
                                        </>
                                    )}
                                />
                            )}
                            {isWhiteLabelApp && hasAudioRecorder && (
                                <RequireAuth
                                    exact
                                    path={UNUPLOADED_SESSIONS_ROUTE}
                                    component={UnuploadedSessions}
                                />
                            )}
                            {/* END AUDIO RECORDER ROUTES */}

                            {/* ONLINE CALL ROUTES */}
                            <Route
                                exact
                                path="/u/:roomName"
                                render={() => (
                                    <>
                                        <UnsupportedBrowserDetection />
                                        <VideoSessionDependencyInjectionProvider
                                            BookSession={BookSession}
                                        >
                                            <UphealerLinkApp
                                                otpAuthState={otpAuthState}
                                                VideoSessionEntryLegacy={
                                                    UphealerLinkAppLegacy
                                                }
                                            />
                                        </VideoSessionDependencyInjectionProvider>
                                    </>
                                )}
                            />

                            {(isDefaultApp || hasOnlineCall) && (
                                <Route
                                    exact
                                    path="/client/:inviteId"
                                    render={(routerProps) => (
                                        <>
                                            <UnsupportedBrowserDetection />
                                            <ClientSignIn {...routerProps} />
                                        </>
                                    )}
                                />
                            )}

                            {(isDefaultApp || hasOnlineCall) && (
                                <Route
                                    exact
                                    path="/client-video"
                                    render={() => (
                                        <>
                                            <UnsupportedBrowserDetection />
                                            <ClientOtpSignIn
                                                otpAuthState={otpAuthState}
                                            />
                                        </>
                                    )}
                                />
                            )}

                            {(isDefaultApp || hasOnlineCall) && (
                                <RequireAuth
                                    exact
                                    path="/video/:contractId"
                                    render={(routerProps) => {
                                        if (
                                            isNewVideoCallingEnabled &&
                                            routerProps.user.privateRoomLink &&
                                            !isWhiteLabelApp
                                        ) {
                                            const searchParams =
                                                new URLSearchParams(
                                                    routerProps.location.search,
                                                );
                                            searchParams.set(
                                                'contractId',
                                                routerProps.match.params
                                                    .contractId,
                                            );

                                            return (
                                                <Redirect
                                                    to={{
                                                        pathname: new URL(
                                                            routerProps.user.privateRoomLink,
                                                        ).pathname,
                                                        search: searchParams.toString(),
                                                    }}
                                                />
                                            );
                                        }

                                        return (
                                            <>
                                                <UnsupportedBrowserDetection />
                                                <VideoSessionDependencyInjectionProvider
                                                    BookSession={BookSession}
                                                >
                                                    <TherapistSessionVideo
                                                        {...routerProps}
                                                    />
                                                </VideoSessionDependencyInjectionProvider>
                                            </>
                                        );
                                    }}
                                />
                            )}
                            {/* END ONLINE CALL ROUTES */}

                            {/* ZOOM ROUTES */}
                            <RequireAuth
                                exact
                                path={ZOOM_APP_HOME_ROUTE}
                                render={({ user }) => (
                                    <ZoomApp
                                        onInit={handleZoomAppInit}
                                        currentProvider={user}
                                    />
                                )}
                                loginPage={{
                                    theme: 'dark',
                                }}
                            />
                            {/* Don't allow routing anywhere else */}
                            {isZoomApp && (
                                <Redirect from="*" to={ZOOM_APP_HOME_ROUTE} />
                            )}
                            <RequireAuth
                                exact
                                path="/zoom-app/auth"
                                component={ZoomAppAuth}
                                loginPage={{
                                    theme: 'dark',
                                }}
                            />
                            <RequireAuth
                                exact
                                path="/zoom-app/install"
                                render={({ user }) => (
                                    <ZoomAppInstall currentProvider={user} />
                                )}
                                loginPage={{
                                    theme: 'dark',
                                }}
                            />
                            {/* END ZOOM ROUTES */}

                            <Route
                                exact
                                path={ERROR_NOT_FOUND_ROUTE}
                                component={Error404}
                            />
                            <Route
                                exact
                                path="/error/:code"
                                component={ErrorGeneric}
                            />

                            {isDefaultApp && (
                                <Route
                                    path={PROGRESS_INSIGHTS_DEMO_ROUTE}
                                    component={ProgressInsightsDemoApp}
                                />
                            )}

                            {isDefaultApp && <Route path="*" component={App} />}

                            {isWhiteLabelApp && (
                                <Route
                                    exact
                                    path="/"
                                    component={WhiteLabelFallbackPage}
                                />
                            )}
                            {isWhiteLabelApp && <Redirect from="*" to="/" />}
                        </Switch>
                    </React.Suspense>
                </GenericErrorBoundary>
            )}
            <DebugModeFloatingButton />
        </div>
    );
};

export default CodeSplittingRouting;
